import { firstValueFrom, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  FormArray,
  UntypedFormBuilder,
  FormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { LoginService } from '@services/login/login.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ValidationService } from '@common_libraries/Services/validation/validation.service';
import { UserService } from '@services/user/user.service';
import { SessionService } from '@services/session/session.service';
import { LocalStorageService } from 'ngx-webstorage';
import { LogoutReasons } from '@common_libraries/lib/Enums';
import { ModuleAuth } from '@common_libraries/lib/ModuleAuth';
import { CompanyModel } from '@pageControls/company-picker/model/company-picker.model';
import { ModalService } from '@common_libraries/controls/modal/services/modal.service';
import {
  debounceTime,
  distinctUntilChanged,
} from 'rxjs/operators';
import { OktaAuthService } from '@common_libraries/Services/login/oktaAuth.service';
import { environment } from '@environments/environment';
import { Dictionary } from '@common_libraries/helpers/Dictionary';
import { ServiceLibrary, ServiceReturn } from '@common_libraries/helpers/ServiceLibrary';

@Injectable()
export class LoginModel {
  loginForm: UntypedFormGroup;
  inProgressFlag: boolean;
  loginErrorMessage: string;
  showErrorMessage = false;
  resetForm: UntypedFormGroup;
  resetInProgressFlag: boolean;
  showResetErrorMessage = false;
  showResetSuccessMessage = false;
  showResetForm: boolean;
  logoutReason: string;
  termsOfUse: TermsOfUseObjectModel;
  public companies: Array<CompanyModel>;
  return_to: string;

  doTermsOfUseUpdate: boolean;
  showPassword: boolean = false;
  userName: string = '';

  isDirectoryLogin: boolean = false;

  pageReady: boolean = false;
  constructor(
    private loginService: LoginService,
    private userService: UserService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private localSt: LocalStorageService,
    private sessionService: SessionService,
    private moduleAuth: ModuleAuth,
    private modalService: ModalService,
    private oktaService: OktaAuthService,
    private servLib: ServiceLibrary
  ) {
    this.loginForm = this.fb.group({});
    this.resetForm = this.fb.group({});

    this.localSt.clear('companyid');
    this.localSt.clear('isloggedin');
    this.localSt.clear('moduleauthdata');
    this.localSt.clear('Theme');

    this.termsOfUse = new TermsOfUseObjectModel();
  }

  public initModel(route: any) {
    this.sessionService.SetIsLoggedIn(false);
    const root = this;

    this.oktaService.oktaReadyObservable.subscribe(async (ready) => {
      if (ready) { 
        console.log('destroying existing authentication');
        root.oktaService.oktaAuth.tokenManager.clear();

        //Clear out the old okta cookie we used to store in our servers
        root.pageReady = true;

        root.setupLogoutReason(route);
        root.buildNextFormGroup('');
        root.buildResetFormGroup();
      }
    });
  }

  setTempCookie() {
    const parameters = new Dictionary([]);

    return this.servLib.HttpPost(
      '/api/Session/Set2',
      ServiceLibrary.ContentType.JSON,
      parameters
    );
  }

  private setupLogoutReason(route) {
    route.params.subscribe((params) => {
      const reasonParam = params['reason'];
      if (reasonParam) {
        const reasonID = Number(params['reason']);
        if (reasonID != null && !Number.isNaN(reasonID)) {
          switch (reasonID) {
            case LogoutReasons.SecurityModule:
              this.logoutReason =
                'You have been logged out due to insufficient privileges or a change in your IP address. Please log in again or contact TigerEye Support';
              break;
            case LogoutReasons.HasPermission:
              this.logoutReason =
                'You have been logged out due to accessing an item without proper authorization. Please try again or contact TigerEye Support';
              break;
            case LogoutReasons.LoginAccessDenied:
              this.logoutReason =
                'You do not have access to the TigerEye2 Beta. Please log in to TigerEye at <a href="https://tigereye.tigerrisk.com">https://tigereye.tigerrisk.com</a>';
              break;
            case LogoutReasons.ShowForgotPwdPage:
              this.toggleResetForm(true);
              break;
            case LogoutReasons.OktaTokenExpired:
              this.logoutReason =
                'Your authentication session has expired.  Please log in again.';
              break;
            case LogoutReasons.OktaTokenRenewError:
              this.logoutReason =
                'Your authentication session was unable to refresh the token and expired.  Please log in again.';
              break;
            case LogoutReasons.OktaTokenExpiredNoCodes:
              this.logoutReason =
                'Your authentication session has expired and no valid token was found.  Please log in again.';
              break;
            case LogoutReasons.IncorrectPasswordUser:
              this.logoutReason =
                'Incorrect Username or Password';
              break;
            case LogoutReasons.ForcePasswordReset:
              this.logoutReason = 'Password Reset Required.';
            default:
              this.logoutReason =
                'Unknown Error. Please Contact TigerEye Support';
              break;
          }
        }
      }
    });
  }

  private buildLoginFormGroup(userName) {
    this.loginForm = this.fb.group({
      username: [
        userName,
        [Validators.required, ValidationService.emailValidator],
      ],
      password: [null, [Validators.required]],
    });
  }

  private buildNextFormGroup(userName) {
    this.loginForm = this.fb.group({
      username: [userName, [Validators.required, ValidationService.emailValidator]],
      password: [null, []],
    });
  }

  public doEmailChangeEvent(event) {  
    if (this.showPassword === true && event.keyCode !== 9) {
      this.buildNextFormGroup(this.loginForm.controls.username.value);
      this.showPassword = false;
    }
  }

  private buildResetFormGroup() {
    this.resetForm = this.fb.group({
      emailUsername: [
        null,
        [
          Validators.required,
          ValidationService.emailValidator,
        ],
      ],
    });
  }

  public async onNextLoginForm(form: UntypedFormGroup) {
    const formValues = form.value;
    var forcePasswordReset: boolean = false;

    if (form.valid) {

      // If return_to is set in querystring, instead of setting up user and company data, we are going to send them back to zendesk
      // because return_to means we are doing a zendesk SSO
      this.return_to = this.route.snapshot.queryParamMap.get('return_to');
      if (this.return_to === undefined || this.return_to === null || this.return_to === '') {
          // return_to is not set, so do our standard login flow
          this.sessionService.ClearZendeskReturnTo();
      } else {
          this.sessionService.SetZendeskReturnTo(this.return_to);
      }

      var haltService =  this.userService.CheckForHalts(formValues.username);
      var haltResult = await (await firstValueFrom<ServiceReturn>(haltService)).json.data.result;
      switch(haltResult)
      {
        case 2:
          forcePasswordReset = true;
          break;
      }

      this.oktaService
        .loginOktaCheck('', formValues.username, formValues.password)
        .then((res: any) => {
          if (res.directoryUser) {

            if (forcePasswordReset) {
              this.modalService.open('force-password-reset-modal');
              return;
            }

            this.isDirectoryLogin = true;
            this.showPassword = true;
            this.buildLoginFormGroup(res.userName);
          } else {
            this.oktaService.systemLinkCheck(formValues.username).subscribe((systemLinked) => {
              if(systemLinked) {
                this.isDirectoryLogin = false;
                this.userAcceptedTerms(formValues.username).subscribe((terms) => {
                  if (terms.accepted || this.doTermsOfUseUpdate) {
                    //console.log('idp hide');
                    this.showPassword = false;                            

                    // kill anything and everything related to a login session before attempting to create a new one
                    this.oktaService.oktaAuth.revokeAccessToken().then(() => {
                      this.oktaService.oktaAuth.closeSession()
                      .then(() => {
                        if (res.idp === environment.Okta_TigerRiskIDP) {
                          // force redirect SAML to the environment's TigerRiskIDP
                          this.oktaService.performSAMLRedirect(res.userName, environment.Okta_TigerRiskSwitchIDP);
                        } else {
                          // for non-TigerRisk users, the idp setting should be valid
                          this.oktaService.performSAMLRedirect(res.userName, res.idp);
                        }                        
                      });
                    });            

                  } else {
                    this.modalService.open('accept-terms-modal');
                  }
                  
                });
              } else {
                this.doTermsOfUseUpdate = false;
                this.showErrorMessage = true;
                this.loginErrorMessage = 'Incorrect Username or Password';
                this.inProgressFlag = false;
              }
            });
          }
        })
        .catch((err: any) => {
          let errorMsg: string = '';
          if (err.errorCode === 'E0000004') {
            errorMsg = 'Incorrect Username or Password';
          }
          this.showPassword = false;
          this.doTermsOfUseUpdate = false;
          this.showErrorMessage = true;
          this.loginErrorMessage = errorMsg;
          this.inProgressFlag = false;
        });
    } else {
      form.markAllAsTouched();
      form.markAsDirty();
    }
  }

  public async onSubmitLoginForm(form: UntypedFormGroup) {
    const formValues = form.value;
    this.showErrorMessage = false;
    var forcePasswordReset: boolean = false;

    var haltService =  this.userService.CheckForHalts(formValues.username);
    var haltResult = await (await firstValueFrom<ServiceReturn>(haltService)).json.data.result;

    switch(haltResult)
    {
      case 2:
        forcePasswordReset = true;
        break;
    }

    if (forcePasswordReset) {
      this.modalService.open('force-password-reset-modal');
      return;
    }

    // kill anything and everything related to a login session before attempting to create a new one
    this.oktaService.oktaAuth.revokeAccessToken().then(() => {
      this.oktaService.oktaAuth.closeSession()
      .then(() => {
        this.userAcceptedTerms(formValues.username).subscribe((terms) => {
          if (terms.accepted || this.doTermsOfUseUpdate) {
            this.oktaService.systemLinkCheck(formValues.username).subscribe((systemLinked) => {
              if(systemLinked) {
                this.oktaService
                  .loginDirectoryUser('', formValues.username, formValues.password)
                  .then((res: any) => {
                    if (res.status === 'SUCCESS') {
                      this.inProgressFlag = true;
                      this.sessionService.SetUserName(formValues.username);
                      this.oktaService.performOktaRedirect(res, formValues.username);
                    } else {
                      this.doTermsOfUseUpdate = false;
                      this.showErrorMessage = true;
                      this.loginErrorMessage = res.status;
                      this.inProgressFlag = false;
                    }
                  })
                  .catch((err: any) => {
                    let errorMsg: string = '';
                    if (err.errorCode === 'E0000004') {
                      errorMsg = 'Incorrect Username or Password';
                    }
        
                    this.doTermsOfUseUpdate = false;
                    this.showErrorMessage = true;
                    this.loginErrorMessage = errorMsg;
                    this.inProgressFlag = false;
                  });
            } else {
              this.doTermsOfUseUpdate = false;
              this.showErrorMessage = true;
              this.loginErrorMessage = 'Incorrect Username or Password';
              this.inProgressFlag = false;
            }
          });
          } else {
            this.modalService.open('accept-terms-modal');
          }
        });
      });
    });

  }

  public sendPasswordReset() {
    this.onSubmitResetForm(null, this.loginForm.value.username);
    this.modalService.close('force-password-reset-modal');
  }

  public onSubmitResetForm(form: UntypedFormGroup, emailUsername: string = null) {
    let resetEmail: string = null;

    if (form !== null && emailUsername === null) {
      const formValues = form.value;
      resetEmail = formValues.emailUsername;
    } else {
      resetEmail = emailUsername;
    }

    this.resetInProgressFlag = true;
    this.showResetErrorMessage = false;
    this.userService.ResetPassword(resetEmail).subscribe(
      (result) => {
        this.showResetSuccessMessage = true;
        this.showResetErrorMessage = false;
        this.resetInProgressFlag = false;
      },
      (err) => {
        this.showResetSuccessMessage = false;
        this.showResetErrorMessage = true;
        this.resetInProgressFlag = false;
      }
    );
  }

  public toggleResetForm(toggle: boolean) {
    this.showResetForm = toggle;

    this.showResetErrorMessage = false;
    this.showResetSuccessMessage = false;
    this.showErrorMessage = false;

    this.loginForm.reset();
    this.resetForm.reset();
  }

  public acceptTermsOfUse() {
    // set skip flag for terms check, and store in sessionStorage for callbacck page to use
    this.doTermsOfUseUpdate = true;
    sessionStorage.setItem('doTermsOfUseUpdate', JSON.stringify(true));

    if (this.isDirectoryLogin) {
      this.onSubmitLoginForm(this.loginForm);
    } else {
      this.onNextLoginForm(this.loginForm);
    }
    
    this.modalService.close('accept-terms-modal');
  }

  public userAcceptedTerms(username: string) {
    const parent = this;
    return Observable.create((observer) => {
      parent.userService
        .AcceptTermsOfUseGetByLogin(username)
        .subscribe((res: any) => {
          parent.termsOfUse = res.json.data;

          observer.next(parent.termsOfUse);
          observer.complete();
        });
    });
  }

  public declineTermsOfUse() {
    this.sessionService.SetIsLoggedIn(false);
    window.location.href = '/login';
  }
}

export class TermsOfUseObjectModel {
  accepted: boolean;
  acceptDetails: AcceptDetailsModel;
}

export class AcceptDetailsModel {
  AcceptDate: string;
  OS: string;
  Browser: string;
  IPAddress: string;
  Version: number;
}
